import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { GetOrganizationResponseSchema } from '@/lib/validations/api-schemas';
import type { GetOrganizationResponse } from '@codemerlin/shared/types/organization';

export const organizationKeys = {
  all: ['organization'] as const,
  detail: (id: string) => [...organizationKeys.all, id] as const,
  current: () => [...organizationKeys.all, 'current'] as const,
};

interface UseOrganizationOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

/**
 * Hook to fetch organization details
 *
 * If organizationId is provided, fetches that specific organization.
 * Otherwise, fetches the current user's organization.
 */
export function useOrganization(organizationId?: string | null, options?: UseOrganizationOptions) {
  const queryOptions: Parameters<typeof useQuery>[0] = {
    queryKey: organizationId ? organizationKeys.detail(organizationId) : organizationKeys.current(),
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');
      const endpoint = `organizations/${organizationId}`;

      return api.get<GetOrganizationResponse>(endpoint, {
        schema: GetOrganizationResponseSchema,
      });
    },
    enabled: options?.enabled !== false,
    staleTime: 30000,
  };

  // Only add refetchInterval if explicitly provided (to avoid undefined)
  if (options?.refetchInterval !== undefined) {
    queryOptions.refetchInterval = options.refetchInterval;
  }

  return useQuery(queryOptions);
}

/**
 * Hook to get the current user's organization from their profile
 * (Faster than fetching if profile is already loaded)
 */
import { useProfile } from './useProfile';

export function useCurrentOrganization() {
  const { data: profile, isLoading, error } = useProfile();

  const organization = profile?.organization
    ? {
        organizationId: profile.organization.organization_id,
        name: profile.organization.name,
      }
    : null;

  return {
    organization,
    isLoading,
    error,
  };
}
